import { UuidNil } from "common/uuid";
import { LOGOUT_USER_ACCOUNT } from "store/actions/Auth";
import { createReducer } from "./Util";
import { Email, EmailReader } from "models/Email";
import {
  DELETE_EMAIL,
  MARK_EMAIL_AS_SPAM,
  GET_CUSTOMER_EMAILS,
  GET_EMAILS,
  GET_EMAILS_AFTER,
  GET_EMAILS_BEFORE,
  GET_EMAILS_BETWEEN,
  GET_EMAILS_UNREAD,
  READ_EMAIL,
  SEND_EMAIL,
  GET_EMAIL,
  GET_EMAIL_THREAD,
  GET_REFERENCE_EMAILS,
  SET_EMAIL_CUSTOMER,
  STAR_EMAIL
} from "store/actions/Emails";

export class EmailState {

  private emails: { [index: string]: Email } | null
  private threads: { [index: string]: string[] }
  private hasFetchedAll: boolean
  private hasFetchedUnread: boolean
  private hasMoreOlder: boolean
  private oldestFetchedDate: number | null

  constructor() {
    this.emails = null
    this.threads = { };
    this.hasFetchedAll = false
    this.hasFetchedUnread = false
    this.hasMoreOlder = true
    this.oldestFetchedDate = null
  }

  public setEmails(emails: Email[]) {
    this.hasFetchedAll = true
    this.emails = {}
    this.threads = {}
    this.oldestFetchedDate = emails.length > 0 ? Math.min(...emails.map(email => email.date)) : null
    emails.forEach(email => {
      this.setEmail(email, false)
    })
    this.rebuildThreads()
    return this
  }

  public addEmails(emails: Email[]) {
    emails.forEach(email => {
      this.setEmail(email, false)
    })
    this.rebuildThreads()
    return this
  }

  public addOlderEmails(emails: Email[]) {
    if (emails.length === 0) {
      this.hasMoreOlder = false
    }
    if (emails.length > 0) {
      const oldest = Math.min(...emails.map(email => email.date));
      this.oldestFetchedDate = this.oldestFetchedDate === null ? oldest : Math.min(this.oldestFetchedDate, oldest);
    }
    return this.addEmails(emails)
  }

  public getThreads(id: string): string[] {
    return this.threads[id] ?? [];
  }
  public updateUnreadEmails(emailsUnread: Email[]) {
    this.hasFetchedUnread = true
    if (this.emails === null) {
      this.emails = {}
    }

    var emailReader = new EmailReader()
    emailReader.ip_address = "0.0.0.0"
    emailReader.date = Date.now().toString()

    Object.values(this.emails).forEach((email) => {
      if (email.reader === null || email.reader.length === 0) {
        email.reader = [emailReader]
        this.setEmail(email, false)
      }

    })

    emailsUnread.forEach(email => {
      email.reader = [];
      this.setEmail(email, false)
    })

    this.rebuildThreads()
    return this
  }

  private rebuildThreads() {
    this.threads = {}
    if (this.emails === null) {
      return
    }

    Object.values(this.emails).forEach(email => {
      if (email.reply_to === UuidNil) {
        return
      }

      if (!this.threads[email.reply_to]) {
        this.threads[email.reply_to] = [];
      }
      if (!this.threads[email.reply_to].includes(email.id)) {
        this.threads[email.reply_to].push(email.id)
      }
    })
  }

  public setEmail(email: Email, rebuildThreads: boolean = true) {
    if (this.emails === null) {
      this.emails = {}
    }

    this.emails[email.id] = email
    if (rebuildThreads) {
      this.rebuildThreads()
    }
    return this
  }

  public getEmailById(id: string): Email | null {
    if (this.emails === null) {
      this.emails = {}
    }

    return this.emails[id] ?? null
  }

  public deleteEmail(id: string) {
    if (this.emails === null) {
      this.emails = {}
    }

    delete this.emails[id]
    this.rebuildThreads()
    return this
  }

  public getEmails(): Email[] {
    if (this.emails === null) {
      this.emails = {}
    }

    return Object.values(this.emails)
  }

  public getNewestEmail(): Email | null {
    if (this.emails === null) {
      this.emails = {}
    }

    const newest = Object.values(this.emails).sort((a, b) => b.date - a.date)[0]
    if (newest === undefined) {
      return null
    }
    return newest
  }

  public getOldestEmail(): Email | null {
    if (this.emails === null) {
      this.emails = {}
    }

    const oldest = Object.values(this.emails).sort((a, b) => a.date - b.date)[0]
    if (oldest === undefined) {
      return null
    }
    return oldest
  }

  public isFetched(): boolean {
    return this.hasFetchedAll || this.hasFetchedUnread
  }

  public isFullyFetched(): boolean {
    return this.hasFetchedAll
  }

  public canFetchMoreOlder(): boolean {
    return this.hasMoreOlder
  }

  public getOldestFetchedDate(): number | null {
    return this.oldestFetchedDate;
  }

}



const Emails = createReducer<EmailState>(new EmailState())
  .handle<Email[]>([GET_EMAILS.SUCCESS], (state, payload) => state.setEmails(payload))
  .handle<Email>([GET_EMAIL.SUCCESS], (state, payload) => state.setEmail(payload))
  .handle<Email[]>([GET_EMAILS_AFTER.SUCCESS, GET_EMAIL_THREAD.SUCCESS, GET_CUSTOMER_EMAILS.SUCCESS, GET_REFERENCE_EMAILS.SUCCESS, GET_EMAILS_BETWEEN.SUCCESS], (state, payload) => state.addEmails(payload))
  .handle<Email[]>([GET_EMAILS_BEFORE.SUCCESS], (state, payload) => state.addOlderEmails(payload))
  .handle<Email[]>([GET_EMAILS_UNREAD.SUCCESS], (state, payload) => state.updateUnreadEmails(payload))
  .handle<Email>([SEND_EMAIL.SUCCESS], (state, payload) => state.setEmail(payload))
  .handle<string>([DELETE_EMAIL.SUCCESS, MARK_EMAIL_AS_SPAM.SUCCESS], (state, payload) => state.deleteEmail(payload))
  .handle<Email>([STAR_EMAIL.SUCCESS], (state, payload) => state.setEmail(payload))
  .handle<Email>([READ_EMAIL.SUCCESS], (state, payload) => state.setEmail(payload))
  .handle<Email>([SET_EMAIL_CUSTOMER.SUCCESS], (state, payload) => state.setEmail(payload))
  .handle([LOGOUT_USER_ACCOUNT.SUCCESS], () => new EmailState());

export default Emails.handler;
